import { Box, ListSubheader, MenuItem } from '@mui/material';

import { type RaSelectOption } from '@modules/_shared/components/form/RaSelect';
import {
  IndicatorLeadStage,
  type IndicatorStatus,
} from '@modules/_shared/components/IndicatorLeadStage';

export const selectInputStyles = {
  borderRadius: '8px',
  transition: 'all 0.15s ease',
  '&:hover': {
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
} as const;

export const getStageBackgroundColor = (
  status: string | null | undefined,
  palette: {
    success: { main: string };
    error: { main: string };
    primary: { main: string };
  },
): string => {
  switch (status) {
    case 'won':
      return `color-mix(in srgb, ${palette.success.main} 10%, transparent)`;
    case 'lost':
      return `color-mix(in srgb, ${palette.error.main} 10%, transparent)`;
    default:
      return `color-mix(in srgb, ${palette.primary.main} 10%, transparent)`;
  }
};

export const getStageCardColors = (
  status: string | null | undefined,
  palette: {
    success: { main: string };
    error: { main: string };
    primary: { main: string };
  },
): { background: string; border: string } => {
  if (status !== 'active' && status !== 'won' && status !== 'lost') {
    return { background: 'transparent', border: 'transparent' };
  }

  const main =
    status === 'won'
      ? palette.success.main
      : status === 'lost'
      ? palette.error.main
      : palette.primary.main;

  return {
    background: getStageBackgroundColor(status, palette),
    border: `color-mix(in srgb, ${main} 45%, transparent)`,
  };
};

type StageWithStatus = {
  id: string;
  status?: string | null;
  label?: string | null;
};

export const createLeadStageRenderValue = (stages: StageWithStatus[]) => {
  return (value: any) => {
    const stage = stages.find(s => s.id === value);

    if (stage == null) {
      return null;
    }

    return (
      <IndicatorLeadStage status={stage.status as IndicatorStatus}>
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {stage.label}
        </Box>
      </IndicatorLeadStage>
    );
  };
};

export const createLeadStageRenderOption = (stages: StageWithStatus[]) => {
  return (option: RaSelectOption) => {
    if (option.isHeader) {
      return <ListSubheader key={option.value}>{option.label}</ListSubheader>;
    }

    const status = stages.find(stage => stage.id === option.value)?.status;

    return (
      <MenuItem
        key={option.value}
        value={option.value ?? ''}
        disabled={option.disabled ?? false}
        sx={{ minHeight: 'auto', fontSize: '0.875rem' }}
      >
        <IndicatorLeadStage status={status as IndicatorStatus}>
          {option.label}
        </IndicatorLeadStage>
      </MenuItem>
    );
  };
};
